import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createVehicleType,
  deleteVehicleType,
  getVehicleTypesList,
  updateVehicleType,
  type VehicleTypeListParams,
  type VehicleTypePayload,
} from "src/services/administrator/vehicleTypeService";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";

export const useQueryGetVehicleTypesList = (
  params: VehicleTypeListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.vehicleTypeList, params, language],
      queryFn: () => getVehicleTypesList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false,
  );
};

export const useMutationCreateVehicleType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: VehicleTypePayload) => createVehicleType(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleTypeList });
      queryClient.invalidateQueries({ queryKey: QueryNames.unitManagementVehicleTypeLookup });
    },
  });
};

export const useMutationUpdateVehicleType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: Partial<VehicleTypePayload>;
    }) => updateVehicleType(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleTypeList });
      queryClient.invalidateQueries({ queryKey: QueryNames.unitManagementVehicleTypeLookup });
    },
  });
};

export const useMutationDeleteVehicleType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteVehicleType(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleTypeList });
      queryClient.invalidateQueries({ queryKey: QueryNames.unitManagementVehicleTypeLookup });
    },
  });
};
